/**
 * Pure helpers for the Tickets page stream/grid view.
 * Extracted from Tickets.tsx to reduce file size.
 */
import type { SLATimer, Ticket } from "@sd/types/ticket";
import { normalizeTicketTypeFromApi } from "@sd/types/ticket";
import type { TicketStatus } from "@sd/constants/ticketStatuses";
import {
  DEFAULT_TICKET_STATUS,
  STATUS_LABELS,
  formatStatusLabel,
  normalizeStatus,
  statusColors,
} from "@sd/constants/ticketStatuses";

export { STATUS_LABELS, formatStatusLabel, normalizeStatus, statusColors };
import { parseIsVipFromApi, parseIsSlaFromApi } from "@sd/lib/ticketApiBooleans";
import { firstNonEmptyString, getUserDisplayFromMap } from "@sd/lib/userDisplayMap";
import { looksLikeOpaqueUserId, pickAssignedFromGridRow, pickRequesterDisplayFromRow } from "@sd/lib/ticketRowMapping";
import { parseAttachmentIdsFromApi } from "@sd/lib/parseAttachmentIds";

// ── Types ──────────────────────────────────────────────────────────────────────

/** Layout row aligned with admin ticket grid + optional full mock Ticket for dialogs. */
export type StreamTicket = {
  id: string;
  /** From API `ui_id` — shown in UI; `id` stays UUID for keys/API. */
  uiId?: string;
  subject: string;
  department: string;
  requester: string;
  status: TicketStatus;
  priority: string;
  priorityId?: string;
  slaStatus: "on_track" | "at_risk" | "breached";
  category: string;
  channel: string;
  tags: string[];
  assignedAgent: string | null;
  description?: string;
  ticketTypeRaw?: string;
  isVip?: boolean;
  isSla?: boolean;
  createdAt?: string;
  updatedAt?: string;
  closedAt?: string;
  resolvedAt?: string;
  deadline?: string;
  mock?: Ticket;
  attachmentIds: string[];
};

// ── Constants ──────────────────────────────────────────────────────────────────

export const SLA_STATUS_LABELS: Record<StreamTicket["slaStatus"], string> = {
  on_track: "On Track",
  at_risk: "At Risk",
  breached: "Breached",
};

/** API `at_risk` → UI "At Risk". */
export function formatSlaStatusLabel(raw: unknown): string {
  const key = String(raw ?? "").toLowerCase().trim().replace(/\s+/g, "_");
  if (!key) {
    return "—";
  }
  const known = SLA_STATUS_LABELS[key as StreamTicket["slaStatus"]];
  if (known) {
    return known;
  }
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

const TITLE_CASE_ACRONYMS: Record<string, string> = {
  it: "IT",
  api: "API",
  hr: "HR",
  ui: "UI",
  sla: "SLA",
  vpn: "VPN",
  wifi: "WiFi",
  email: "Email",
};

function titleCaseWord(word: string): string {
  const lower = word.toLowerCase();
  if (TITLE_CASE_ACRONYMS[lower]) {
    return TITLE_CASE_ACRONYMS[lower];
  }
  if (!lower) {
    return word;
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** API `hardware issue` → UI "Hardware Issue"; `it support` → "IT Support". */
export function formatTitleCase(raw: unknown): string {
  const t = String(raw ?? "").trim();
  if (!t) {
    return "—";
  }
  if (t === "—" || t === "–" || t === "-") {
    return t;
  }

  return t
    .split(/\s+/)
    .map(word =>
      word.includes("-")
        ? word.split("-").map(titleCaseWord).join("-")
        : titleCaseWord(word)
    )
    .join(" ");
}

/** API `p3 - medium` → UI "P3 - Medium". */
export function formatPriorityLabel(raw: unknown): string {
  const t = String(raw ?? "").trim();
  if (!t) {
    return "—";
  }
  if (t === "—" || t === "–" || t === "-") {
    return t;
  }
  return formatTitleCase(t);
}

export const priorityColors: Record<string, string> = {
  P1: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "P1 - Critical": "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  P2: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  "P2 - High": "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  P3: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  "P3 - Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  P4: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  "P4 - Low": "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

/** Maps short priority dropdown values to the full API strings e.g. "P2 - High" */
export const PRIORITY_API_MAP: Record<string, string> = {
  "P1": "P1 - Critical",
  "P2": "P2 - High",
  "P3": "P3 - Medium",
  "P4": "P4 - Low",
};

/** Parse SLA Policy grid/list payload into row objects. */
export function extractSlaPolicyGridRows(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }
  if (payload && typeof payload === "object") {
    const grid = (payload as Record<string, unknown>).grid_data;
    if (Array.isArray(grid)) {
      return grid as Record<string, unknown>[];
    }
  }
  return [];
}

/** Map filter bucket P1…P4 → SLA Policy row UUID for API `priority_id`. */
export function buildPriorityIdByBucket(
  rows: Record<string, unknown>[]
): Record<string, string> {
  const byBucket: Record<string, string> = {};
  const byLabel: Record<string, string> = {};

  for (const row of rows) {
    const id = row.id != null ? String(row.id).trim() : "";
    const label = row.priority != null ? String(row.priority).trim() : "";
    if (!id || !label) continue;

    byLabel[label] = id;
    const bucket = priorityBucket(label);
    if (!byBucket[bucket]) {
      byBucket[bucket] = id;
    }
  }

  for (const bucket of ["P1", "P2", "P3", "P4"]) {
    if (byBucket[bucket]) continue;
    const label = PRIORITY_API_MAP[bucket];
    if (label && byLabel[label]) {
      byBucket[bucket] = byLabel[label];
    }
  }

  return byBucket;
}

/** Resolve P1…P4 filter to SLA Policy UUID (e.g. 26b2da17-853f-4bcd-923b-4bf3ab30ea4e). */
export function resolvePriorityIdForFilter(
  priorityFilter: string,
  priorityIdByBucket: Record<string, string>
): string | undefined {
  if (priorityFilter === "all") return undefined;
  return (
    priorityIdByBucket[priorityFilter] ??
    priorityIdByBucket[priorityBucket(priorityFilter)]
  );
}

/** Client-side priority filter — matches UUID (`priority_id`) or label bucket P1…P4. */
export function ticketPriorityMatchesFilter(
  ticket: { priority?: string; priorityId?: string },
  filterValue: string,
  priorityIdByBucket: Record<string, string>
): boolean {
  if (filterValue === "all") return true;
  const targetId = resolvePriorityIdForFilter(filterValue, priorityIdByBucket);
  if (targetId && ticket.priorityId === targetId) return true;
  return priorityBucket(String(ticket.priority ?? "")) === priorityBucket(filterValue);
}

// ── Normalizers ────────────────────────────────────────────────────────────────

/** Match filter "P1"…"P4" against API/mock labels like "P3 - Medium" */
export function priorityBucket(p: string): string {
  const m = String(p).toUpperCase().match(/P\s*([1-4])\b/);
  return m ? `P${m[1]}` : "P3";
}

export function normalizePriority(p: unknown): string {
  const raw = typeof p === "number" ? `P${p}` : String(p ?? "").trim();
  if (!raw || raw === "—") return "P3";
  const upper = raw.toUpperCase();
  const lead = upper.match(/^P\s*([1-4])/);
  if (lead) return `P${lead[1]}`;
  const m = upper.match(/^P([1-4])$/);
  if (m) return `P${m[1]}`;
  const n = upper.match(/^([1-4])$/);
  if (n) return `P${n[1]}`;
  if (/CRITICAL|^1$/i.test(upper)) return "P1";
  if (/HIGH|^2$/i.test(upper)) return "P2";
  if (/MEDIUM|^3$/i.test(upper)) return "P3";
  if (/LOW|^4$/i.test(upper)) return "P4";
  return priorityBucket(raw);
}

// ── Row builders ───────────────────────────────────────────────────────────────

export function enrichStreamRowsWithPriorityMap(
  rows: StreamTicket[],
  labelById: Map<string, string>
): StreamTicket[] {
  if (labelById.size === 0) return rows;
  return rows.map(row => {
    if (row.priorityId) {
      const fromId = labelById.get(row.priorityId);
      if (fromId) {
        return { ...row, priority: fromId };
      }
    }
    return row;
  });
}

export function enrichStreamRowsWithUserMap(
  rows: StreamTicket[],
  map: Map<string, string>,
  agents: { id: string; name: string }[] = []
): StreamTicket[] {
  if (map.size === 0 && agents.length === 0) return rows;
  return rows.map(row => {
    let req = row.requester;
    if (req && req !== "—" && looksLikeOpaqueUserId(req)) {
      req = getUserDisplayFromMap(map, req) ?? req;
    }
    let agent = row.assignedAgent;
    if (agent && looksLikeOpaqueUserId(agent)) {
      agent =
        agents.find(a => a.id === agent)?.name ??
        getUserDisplayFromMap(map, agent) ??
        agent;
    }
    if (req === row.requester && agent === row.assignedAgent) return row;
    return { ...row, requester: req, assignedAgent: agent };
  });
}

export function rowFromUnknown(raw: unknown): StreamTicket {
  const r = (raw ?? {}) as Record<string, unknown>;
  const id = String(r.id ?? r.ticket_id ?? r.reference ?? r.pk ?? r.uuid ?? "").trim();
  const uiRaw = r.ui_id ?? r.uiId;
  const uiId =
    uiRaw !== null && uiRaw !== undefined && String(uiRaw).trim() !== ""
      ? String(uiRaw).trim()
      : undefined;
  const subject =
    String(r.subject ?? r.title ?? r.summary ?? "").trim() ||
    `Ticket ${id || "untitled"}`;

  const picked = pickRequesterDisplayFromRow(r);
  const requester = picked.trim() !== "" ? picked.trim() : "—";
  const department = String(r.department ?? r.dept ?? r.user_department ?? "—") || "—";

  const assigneeName = firstNonEmptyString(
    r.assigned_to_name,
    r.assigned_to_display_name,
    r.assigned_name,
    r.assignee_name,
    r.assigned_agent_name,
    r.assigned_user_name
  );
  const assignedFromRow = pickAssignedFromGridRow(r);
  const assignedAgent = assigneeName || assignedFromRow || null;

  const slaRaw = String(r.sla_status ?? r.slaStatus ?? r.sla ?? "").toLowerCase();
  let slaStatus: StreamTicket["slaStatus"] = "on_track";
  if (slaRaw.includes("breach")) slaStatus = "breached";
  else if (slaRaw.includes("risk")) slaStatus = "at_risk";

  const description =
    typeof r.description === "string"
      ? r.description
      : r.description != null
        ? String(r.description)
        : undefined;
  const ticketTypeRawRaw = r.tickettype ?? r.ticket_type ?? r.tickittype;
  const ticketTypeRaw =
    ticketTypeRawRaw != null && String(ticketTypeRawRaw).trim()
      ? String(ticketTypeRawRaw).trim()
      : undefined;
  const isVip = parseIsVipFromApi(r.is_vip, r.isVip);
  const isSla = parseIsSlaFromApi(r.is_sla, r.isSla);
  const createdAt =
    typeof r.created_on === "string" ? r.created_on :
    typeof r.createdAt === "string" ? r.createdAt : undefined;
  const updatedAt =
    typeof r.updated_on === "string" ? r.updated_on :
    typeof r.updatedAt === "string" ? r.updatedAt : undefined;
  const closedAtRaw = firstNonEmptyString(r.closed_at, r.closedAt, r.closed_on, r.close_date);
  const resolvedAtRaw = firstNonEmptyString(r.resolvedtime, r.resolved_at, r.resolvedAt, r.resolved_on);
  const deadline = typeof r.deadline === "string" ? r.deadline : undefined;
  const category =
    r.category != null && String(r.category).trim() !== ""
      ? String(r.category).trim()
      : String(r.tickettype ?? r.tickittype ?? "—") || "—";

  return {
    id: id || `row-${Math.random().toString(36).slice(2)}`,
    uiId,
    subject,
    department,
    requester,
    status: (() => {
      const s = normalizeStatus(r.status);
      return !assignedAgent && s === "pending_from_client"
        ? DEFAULT_TICKET_STATUS
        : s;
    })(),
    priority: String(r.priority ?? "").trim() || "—",
    priorityId: r.priority_id ? String(r.priority_id).trim() : undefined,
    slaStatus,
    category,
    channel: String(r.channel ?? "—") || "—",
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    assignedAgent,
    description,
    ticketTypeRaw,
    isVip,
    isSla,
    createdAt,
    updatedAt,
    closedAt: closedAtRaw ?? undefined,
    resolvedAt: resolvedAtRaw ?? undefined,
    deadline,
    mock: undefined,
    attachmentIds: parseAttachmentIdsFromApi(r.attachments),
  };
}

export function defaultSlaTimersFromStream(row: StreamTicket): SLATimer[] {
  if (row.slaStatus === "breached") {
    return [{ type: "first_response", label: "First Response", time: "Overdue", isBreached: true, state: "breached" }];
  }
  if (row.slaStatus === "at_risk") {
    return [{ type: "first_response", label: "First Response", time: "At risk", isBreached: false, state: "active", atRisk: true }];
  }
  return [{ type: "first_response", label: "First Response", time: "On track", isBreached: false, state: "active" }];
}

export function streamRowToTicket(row: StreamTicket): Ticket {
  const mock = row.mock;
  const pri = normalizePriority(row.priority) as Ticket["priority"];
  const priorityLabel =
    row.priority && row.priority !== "—"
      ? formatPriorityLabel(row.priority)
      : undefined;
  const ticketType = mock?.ticketType ?? normalizeTicketTypeFromApi(row.ticketTypeRaw);
  return {
    id: row.id,
    uiId: row.uiId ?? mock?.uiId,
    title: row.subject,
    description: row.description ?? mock?.description,
    status: row.status,
    priority: pri,
    priorityId: row.priorityId,
    priorityLabel,
    ticketType,
    ticketTypeRaw: row.ticketTypeRaw,
    isVip: row.isVip ?? mock?.isVip,
    isSla: row.isSla ?? mock?.isSla,
    user: mock?.user ?? {
      id: "—",
      name: row.requester,
      email: "",
      company: row.department,
      department: row.department,
      phone: "",
      timezone: "UTC",
      supportTier: "",
    },
    assignee: row.assignedAgent ?? undefined,
    overdue: mock?.overdue ?? (row.slaStatus === "breached" ? "SLA overdue" : undefined),
    overdueLevel: mock?.overdueLevel ?? (row.slaStatus === "breached" ? "critical" : "none"),
    notes: mock?.notes ?? [],
    slaTimers: mock?.slaTimers ?? defaultSlaTimersFromStream(row),
    attachments: mock?.attachments,
    attachmentFileIds: row.attachmentIds.length > 0 ? row.attachmentIds : undefined,
    category: row.category && row.category !== "—" ? row.category : mock?.category,
    tags: row.tags,
    channel: row.channel && row.channel !== "—" ? row.channel : mock?.channel,
    createdAt: row.createdAt ?? mock?.createdAt ?? new Date().toISOString(),
    updatedAt: row.updatedAt ?? mock?.updatedAt ?? new Date().toISOString(),
    closedAt: row.closedAt ?? mock?.closedAt,
    resolvedAt: row.resolvedAt ?? mock?.resolvedAt ?? mock?.resolution?.resolvedAt,
    deadline: row.deadline ?? mock?.deadline,
  };
}

export function parseTicketGridTotal(payload: unknown): number | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const o = payload as Record<string, unknown>;
  const totalKeys = new Set([
    "count",
    "total",
    "total_count",
    "total_tickets",
    "grand_total",
  ]);

  for (const [key, raw] of Object.entries(o)) {
    const normalized = key.toLowerCase().replace(/[\s-]+/g, "_");
    if (!totalKeys.has(normalized)) continue;
    if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
      return Math.floor(raw);
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      const n = Number.parseInt(raw, 10);
      if (!Number.isNaN(n) && n >= 0) return n;
    }
  }
  return undefined;
}

// ── Attachment helpers ───────────────────────────────────────────────────────

/**
 * Normalize attachments field before sending to API:
 * - Array / JSON-string-array of file objects or UUIDs → JSON string of all UUIDs
 * - Single UUID string → keep as-is
 * - Empty / null → null
 */
export function normalizeAttachmentsForApi(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (value === "[]" || value === "") return null;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const ids = parsed
          .map((v: unknown) => (typeof v === "string" ? v : (v as any)?.id ?? null))
          .filter(Boolean);
        if (ids.length === 0) return null;
        if (ids.length === 1) return ids[0];
        return JSON.stringify(ids);
      }
    } catch { /* not JSON */ }
    return value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const ids = value
      .map((v: unknown) => (typeof v === "string" ? v : (v as any)?.id ?? null))
      .filter(Boolean);
    if (ids.length === 0) return null;
    if (ids.length === 1) return ids[0];
    return JSON.stringify(ids);
  }

  if (typeof value === "object" && "id" in (value as any)) return (value as any).id;
  return value;
}
